import Map from 'ol/Map';
import View, { type ViewOptions } from 'ol/View';
import { defaults as defaultControls } from 'ol/control';
import 'ol/ol.css';
import { type Projection, transform } from 'ol/proj';
import {
  forwardRef,
  ReactNode,
  Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import {
  OpenLayersContextInterface,
  OpenLayersProvider,
  useOpenLayersContext,
} from './OpenLayersContext';
import { DrawingProvider, useDrawingContext } from './drawing/DrawingContext';
import { defaults as defaultInteractions, DragPan } from 'ol/interaction';
import { LayerProvider } from './layerManagement/LayerContext';
import { cn } from '@ais-ui/utils';
import { MapStateProvider } from './mapStates/MapStateProvider';

interface MapContainerProps extends Pick<ViewOptions, 'zoom' | 'center'> {
  children?: ReactNode;
  placeholder?: ReactNode;
  setZoom?: (zoom: number) => void;
  setCenter?: (center: [number, number]) => void;
  mapProjection?: Projection | string;
  userProjection?: Projection | string;
  className?: string;
  olMapRef?: React.MutableRefObject<any>; // Access to map outside provider
}

const LayerComponent = ({ children }: { children: ReactNode }) => {
  const { map } = useOpenLayersContext();
  const { isDrawing } = useDrawingContext();

  return (
    <LayerProvider map={map} isDrawing={isDrawing}>
      {children}
    </LayerProvider>
  );
};

function MapContainerComponent(
  {
    children,
    placeholder,
    setZoom,
    setCenter,
    mapProjection = 'EPSG:3857',
    userProjection = 'EPSG:4326',
    zoom = 9,
    center = [11, 59],
    className = '',
    olMapRef,
  }: MapContainerProps,
  forwardedRef: Ref<Map | null>
) {
  const [context, setContext] = useState<OpenLayersContextInterface | null>(
    null
  );
  useImperativeHandle(forwardedRef, () => context?.map ?? null, [context]);

  const mapRef = useCallback((node: HTMLDivElement | null) => {
    if (node !== null && context === null) {
      const map = new Map({
        controls: defaultControls({ zoom: false }),
        target: node,
        view: new View({
          projection: mapProjection,
          center: transform(center, userProjection, mapProjection),
          zoom,
          constrainResolution: true,
        }),
        layers: [],
        interactions: defaultInteractions().extend([
          new DragPan({
            condition: function (event) {
              // Makes panning available for middle mouse button
              return event.originalEvent.button === 1;
            },
          }),
        ]),
      });

      // Do ONLY use this mapref if the map isn't accessible through the provider / useOpenlayers hook
      if (olMapRef) {
        olMapRef.current = map;
      }

      setContext({
        map,
        mapProjection,
        userProjection,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (context === null) {
      return;
    }

    const handleMoveEnd = () => {
      if (setZoom) {
        const currentZoom = context.map.getView().getZoom();
        if (currentZoom) {
          setZoom(currentZoom);
        }
      }

      if (setCenter) {
        const currentCenter = context.map.getView().getCenter();
        if (currentCenter) {
          const transformedCenter = transform(
            currentCenter,
            context.mapProjection,
            context.userProjection
          ) as [number, number];
          setCenter(transformedCenter);
        }
      }
    };

    context.map.on('moveend', handleMoveEnd);

    return () => {
      context.map.un('moveend', handleMoveEnd);
    };
  }, [context, setZoom, setCenter]);

  useEffect(() => {
    return () => {
      context?.map.setTarget(undefined);
    };
  }, [context]);

  useEffect(() => {
    if (context == null || zoom == null) {
      return;
    }
    context.map.getView().setZoom(zoom);
  }, [context, zoom]);

  useEffect(() => {
    if (context == null || center == null) {
      return;
    }
    context.map
      .getView()
      .setCenter(
        transform(center, context.userProjection, context.mapProjection)
      );
  }, [center, context]);

  return (
    <div
      id="map"
      ref={mapRef}
      className={cn('flex w-full h-full relative', className)}
    >
      {context != null ? (
        <OpenLayersProvider value={context}>
          <MapStateProvider>
            <DrawingProvider>
              <LayerComponent>{children}</LayerComponent>
            </DrawingProvider>
          </MapStateProvider>
        </OpenLayersProvider>
      ) : (
        placeholder ?? null
      )}
    </div>
  );
}

export const MapContainer = forwardRef(MapContainerComponent);
